import Hotjar from "@hotjar/browser";
import {
  HOTJAR_MASK_ATTR,
  CONTENTSQUARE_MASK_ATTR,
  maskElement,
  unmaskElement,
} from "./replay-mask";
import { hasBrowserOptOutSignal, isRecordingBlockedOnThisRuntime, isRecordingOff } from "./replay-consent";

/*
 * Session replay (Hotjar, with Contentsquare loaded by Hotjar's loader) —
 * DENY BY DEFAULT, ON A FENCED JOURNEY.
 *
 * M3 (approved 2026-10-19, notice plus opt-out): Hotjar may record the new-user
 * journey — the routes in REPLAY_ALLOWED_ROUTES below, each matched whole with
 * shape-checked segments — unless the visitor turned recording off, the browser
 * sends Global Privacy Control, or the build does not carry the feature. The
 * start condition, the fence and the start itself live in replay-document.js;
 * the choice lives in replay-consent.js. This file is still the ONLY importer
 * of the recorder, and the route gate below never starts anything —
 * startSessionReplay() does, and only replay-document.js calls it (pinned by
 * session-replay.import-invariant.test).
 *
 * Hotjar was initialized unconditionally in App.jsx, login.jsx and
 * PhoneAuthSection.jsx, which meant it recorded every route in the product:
 * exam questions (proprietary content), the student's own written reasoning on
 * the Guided Learning deck, other students' data on the mentorship screens, the
 * admin consoles, and password-reset flows.
 *
 * GTM and the Facebook Pixel are withheld from SOME of those surfaces — and the
 * original version of this comment said "already withheld from those surfaces"
 * flatly, which is not true and would let a reviewer under-rate the residual
 * risk. public/index.html suppresses them only when Guided Learning is enabled
 * AND the audience allows AND the route is sensitive, over a shorter list
 * (/exam, /deck, /gl/deck, /drive-mode, /gl/review). In today's GL-dark posture
 * that condition is false, so they are NOT withheld. This module does not
 * inherit that protection; it is the protection.
 *
 * TWO LISTS, ON PURPOSE. The allow-list alone would be sufficient today, so the
 * deny-list looks redundant — it is not. It is an interlock: the allow-list is
 * the thing a future contributor will edit to "just add one more screen", and a
 * careless pattern there (`^/` or `/e`) silently opens every denied route. Deny
 * is checked first and cannot be widened by an allow-list edit.
 */

// A quiz id is the backend's java.util.UUID (QLearn-service BaseDocument.id),
// serialized lowercase. A position or tutor/guide flag is a small number.
const QUIZ_ID = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const NUMBER = "[0-9]{1,3}";

// Replay may run on exactly these routes. Each pattern is anchored and matched
// against the normalized path (lower-case, no trailing slash, no query or
// fragment); query keys are checked per route in replay-document.js. Anything
// absent is denied: /quiz-history, /overall-performance, /user-settings, the
// legal pages and every admin or tutor console are NOT recorded, and moving to
// one from a recording page is a full page load.
export const REPLAY_ALLOWED_ROUTES = [
  { name: "root", pattern: /^\/$/ }, // redirects in place to /login or /create-quiz
  { name: "register", pattern: /^\/register$/ }, // redirects in place to /login
  { name: "login", pattern: /^\/login$/ },
  { name: "daily", pattern: /^\/daily-absite-question$/ },
  { name: "create-quiz", pattern: /^\/create-quiz$/ },
  { name: "exam", pattern: new RegExp(`^/exam/${QUIZ_ID}/${NUMBER}$`) },
  { name: "exam-tutor", pattern: new RegExp(`^/exam/${QUIZ_ID}/${NUMBER}/tutor/${NUMBER}$`) },
  { name: "exam-guide", pattern: new RegExp(`^/exam/${QUIZ_ID}/${NUMBER}/tutor/${NUMBER}/guide/${NUMBER}$`) },
  { name: "exam-review", pattern: new RegExp(`^/exam/${QUIZ_ID}/${NUMBER}/review$`) },
  { name: "result", pattern: new RegExp(`^/result/${QUIZ_ID}$`) },
  { name: "subscriptions", pattern: /^\/subscriptions$/ },
  { name: "plans", pattern: /^\/plans$/ },
];

// Replay must NEVER run on these, whatever the allow-list says.
//
// /exam is NOT on this list since M3: quiz pages record (with shape-checked
// ids, above) so the journey through a first quiz can be seen. Question
// content stays masked element by element — the stem, options, explanations
// and every review-drawer pane carry data-hj-suppress (exam-question-container,
// exam-review-drawer, exam-result-table and friends) — so a recording shows
// the page around the question, never the question. A new component that
// renders question content outside those boxes would send licensed content to
// both vendors: mask it, or keep /exam off the allow-list.
export const REPLAY_DENIED_PREFIXES = [
  "/gl", // Guided Learning deck — the student's own written reasoning
  "/question", // single-question viewer: proprietary content, share token
  "/qlink", // shared-question preview: proprietary content
  "/absite-diagnostic", // live diagnostic + per-student report; programCode in the query
  "/notes-to-quiz", // one-time token is IN the path
  "/mentorships", // another person's performance data
  "/live", // live session content
  "/live-session", // NOT covered by "/live": prefixes match whole segments
  "/admin", // review queues, curation consoles
  "/admin-portal",
  "/feedback-dashboard",
  "/manage-plans", // AdminPlanView: plan CRUD + the student-invite form
  "/program-metrics",
  "/user-settings", // name, email, phone
  "/reset-password",
  "/forget-password",
  "/verify-user",
  // The OAuth landing is /socialAuth?token=<JWT> — a live session credential in
  // the URL, and Hotjar records URLs, which data-hj-suppress cannot redact. The
  // same-tab flow usually redirects away before the script loads, but the popup
  // branch holds the URL for a 300ms timeout with the script warm, and the
  // status pages the route shows instead of navigating (a sign-in that didn't
  // finish, or one it refused) stay put. Short status pages: no replay value
  // next to a live credential. Denied outright rather than left to timing.
  "/socialauth",
  "/payment",
  "/refund",
];

// React Router v5 matches paths CASE-INSENSITIVELY unless `sensitive` is set,
// and no route in this app sets it. `/EXAM/123/1` therefore renders the exam
// page — so a case-sensitive deny check would be trivially bypassed by a link
// with different casing. Normalize before matching, and strip query/hash so a
// token in `?t=` cannot smuggle a path past the comparison.
export const normalizePath = (pathname) => {
  if (typeof pathname !== "string") return null;
  const cut = pathname.split(/[?#]/)[0];
  if (!cut.startsWith("/")) return null;
  let out = cut.toLowerCase();
  while (out.length > 1 && out.endsWith("/")) out = out.slice(0, -1);
  return out;
};

// Segment-aware: `/plans` matches `/plans` and `/plans/pro`, but `/plan-b` and
// `/plansomething` do NOT. A bare `startsWith` would match all four.
const underPrefix = (path, prefix) =>
  path === prefix || path.startsWith(`${prefix}/`);

const isDenied = (path) => REPLAY_DENIED_PREFIXES.some((p) => underPrefix(path, p));

/*
 * A denied path can arrive as a QUERY VALUE, and body suppression does not cover
 * it: Hotjar records the page URL, and `data-hj-suppress` is a DOM attribute.
 *
 * The live case: a signed-out student opens an emailed notes-to-quiz link.
 * `/notes-to-quiz/<token>` is denied and correctly records nothing — then
 * notes-to-quiz-claim.jsx redirects to
 * `/login?next=%2Fnotes-to-quiz%2F<TOKEN>`, which IS allowed, so the recorder
 * starts on a URL containing the one-time token. The deny-list comment claimed
 * that token was protected; it was not.
 */
/*
 * Only SECRET-BEARING denied paths poison a URL. A denied path in ?next= matters
 * when the path IS the credential; it does not matter for an ordinary authed
 * screen. The first version denied any of them, which took
 * /login?next=/absite-diagnostic dark — a real signed-out acquisition flow
 * (absite-diagnostic.jsx pushes exactly that URL from Start Diagnostic), and
 * the sign-in step is where that funnel drops off.
 */
const SECRET_BEARING_PREFIXES = ["/notes-to-quiz", "/socialauth", "/verify-user"];

const carriesDeniedPath = (raw) => {
  if (typeof raw !== "string") return false;
  // Split on ? OR #: the fragment carries values too, and an earlier version
  // returned early when there was no "?" at all, so a denied path in a hash was
  // never examined.
  const cut = raw.search(/[?#]/);
  if (cut === -1) return false;
  const params = raw.slice(cut + 1).split(/[&#]/);
  return params.some((pair) => {
    const value = pair.slice(pair.indexOf("=") + 1);
    if (!value) return false;
    let decoded = value;
    try { decoded = decodeURIComponent(value.split("+").join(" ")); } catch { /* keep raw */ }
    const candidate = normalizePath(decoded);
    return candidate !== null && SECRET_BEARING_PREFIXES.some((p) => underPrefix(candidate, p));
  });
};

/*
 * A denied path can also be WAITING, not yet in the URL.
 *
 * The ?next= interlock closes the hop it was written for, and the token survives
 * one hop further: Google sign-in stashes the post-login path in localStorage and
 * does a full-page redirect, the round-trip lands on BARE /login (allowed, so the
 * recorder starts), and only then does goToPostLoginPath() history.push the
 * token path into a recorder that is already running. Hotjar records URLs and
 * data-hj-suppress cannot redact one — this module says so itself — so the fix
 * has to be not starting, rather than suppressing after.
 *
 * Read-only, and fails open on a storage error: this is a coverage control on
 * top of deny-by-default, never the only thing between a token and a recording.
 */
const OAUTH_RETURN_PATH_KEY = "oauthReturnPath";

const pendingReturnIsDenied = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const stashed = window.localStorage.getItem(OAUTH_RETURN_PATH_KEY);
    if (!stashed) return false;
    const candidate = normalizePath(stashed);
    return candidate !== null && isDenied(candidate);
  } catch {
    return false;
  }
};

/*
 * The route an address belongs to, or null when replay may not run there.
 * Deny is checked FIRST: an allow-list edit cannot open a denied path. Then a
 * secret-bearing path smuggled through a query or fragment value. Then the
 * anchored allow-list patterns, so nothing under or beside an allowed route is
 * ever covered (/login/extra, /exam/<not a quiz id>/1, /result/latest).
 * Pure: reads nothing but the string.
 */
export const matchReplayRoute = (address) => {
  const path = normalizePath(address);
  if (path === null) return null;
  if (isDenied(path)) return null;
  if (carriesDeniedPath(address)) return null;
  const route = REPLAY_ALLOWED_ROUTES.find((r) => r.pattern.test(path));
  return route ? route.name : null;
};

export const isReplayPathAllowed = (address) => matchReplayRoute(address) !== null;

// The route gate's per-page view also refuses while a denied return path is
// stashed (see pendingReturnIsDenied). The start rule treats any stashed
// return as "not yet" on its own (replay-document.js, rule 4).
export const isReplayAllowed = (pathname) => {
  if (!isReplayPathAllowed(pathname)) return false;
  if (pendingReturnIsDenied()) return false;
  return true;
};

/*
 * A running recorder cannot be stopped — Hotjar has no `pause`. So the gate has
 * to work in two directions:
 *
 *   entry   — never initialize while on a denied route;
 *   in-flight — a session that started on an allowed route and then navigated
 *               into a denied one gets both mask attributes on <body>, which
 *               the recorders honour per element at capture time and which
 *               every descendant inherits, portals included.
 *
 * Since M3 the first line of defence for the in-flight direction is the fence
 * (replay-document.js): on a recording page every move to an address that is
 * not on the journey is a full page load, so the page never reaches another
 * route in place. The body suppression stays as defence in depth behind it.
 */
// Fields the student types into; MUI select displays and option lists (their
// chosen value renders outside the masked <input>: a tutor's name in the
// tag-tutor dialog, for one); and embedded media, wherever it renders: question
// images arrive as data: URLs, and a PDF explanation renders to a canvas (review
// of #866: an enlarged question image rendered in a portal outside the masked
// question area). The app's own pictures (logos, icons) are bundled files, not
// data: or blob: URLs, so they stay visible.
export const MASK_SELECTOR = [
  "input",
  "textarea",
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[aria-haspopup="listbox"]',
  '[role="listbox"]',
  'img[src^="data:"]',
  'img[src^="blob:"]',
  "canvas",
  "video",
].join(", ");
// Older JSX sets only Hotjar's attribute; the mirror gives those elements
// Contentsquare's too while a recording runs.
const HOTJAR_ONLY_SELECTOR = `[${HOTJAR_MASK_ATTR}]:not([${CONTENTSQUARE_MASK_ATTR}])`;

let started = false;
let observer = null;

// Test seam only. Production code never calls this.
export const __resetSessionReplay = () => {
  started = false;
  if (observer) observer.disconnect();
  observer = null;
  if (typeof document !== "undefined" && document.body) {
    unmaskElement(document.body);
  }
};

const maskWithin = (root) => {
  if (!root || typeof root.querySelectorAll !== "function") return;
  if (root.matches?.(MASK_SELECTOR) || root.matches?.(HOTJAR_ONLY_SELECTOR)) {
    maskElement(root);
  }
  root.querySelectorAll(MASK_SELECTOR).forEach(maskElement);
  root.querySelectorAll(HOTJAR_ONLY_SELECTOR).forEach(maskElement);
};

// Every field the student types into is masked, everywhere — the account's
// masking setting is a dashboard toggle nobody in this repo can enforce or
// review, so it is done in code where a test can hold it. The observer is only
// installed once replay is actually running, so users who never reach an
// allowed route pay nothing for it. It also watches the Hotjar attribute
// itself, so an element that gains it later gains Contentsquare's too.
const installInputMasking = () => {
  if (typeof document === "undefined" || !document.body) return;
  maskWithin(document.body);
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver((records) => {
    records.forEach((record) => {
      if (record.type === "attributes") {
        if (record.target && record.target.nodeType === 1) maskWithin(record.target);
        return;
      }
      record.addedNodes.forEach((node) => {
        if (node.nodeType === 1) maskWithin(node);
      });
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [HOTJAR_MASK_ATTR, "aria-haspopup", "role", "contenteditable", "src"],
  });
};

/*
 * Call on mount and on every navigation. Per-route body suppression only: it
 * NEVER starts the recorder (see startSessionReplay). Returns true when replay
 * is running and permitted on this path. Never throws: it runs during app
 * bootstrap, and an analytics vendor failing must not white-screen the product.
 *
 * A recording page is fenced (replay-document.js), so it never reaches a route
 * off the journey in place; this suppression is defence in depth behind that.
 */
export const syncSessionReplay = (pathname) => {
  try {
    // Default-off containment: SPA history changes can expose grant URLs to an
    // already loaded recorder. Body masking does not mask URLs. The reviewed
    // boundary is replay-document.js; with the build flag off nothing records.
    const enabled = process.env.REACT_APP_HOTJAR_ENABLED === "true";
    const allowed = enabled && isReplayAllowed(pathname);

    if (typeof document !== "undefined" && document.body) {
      if (allowed) unmaskElement(document.body);
      else maskElement(document.body);
    }

    if (!allowed) {
      // Body content is suppressed; a previously loaded recorder can still
      // observe URLs. Keep the observer off pages that are not recorded.
      if (observer) { observer.disconnect(); observer = null; }
      return false;
    }
    if (started) {
      installInputMasking();
      return true;
    }
    return false;
  } catch (e) {
    // Analytics failure must never block the learner or the app bootstrap.
    return false;
  }
};

/*
 * Start the recorder. ONLY replay-document.js calls this, once the start
 * condition holds and the page is fenced. Input masking is installed before
 * Hotjar.init so no field is ever captured unmasked. Hotjar is used for init
 * only: no identify, events, attributes or stateChange (the privacy policy
 * says "We do not link recordings to your QLearn account."). Never throws.
 */
export const startSessionReplay = () => {
  try {
    if (started) return true;
    if (process.env.REACT_APP_HOTJAR_ENABLED !== "true") return false;
    // The last gate before the vendor script: never in the native app or
    // behind the third-party kill switch, whoever calls this.
    if (isRecordingBlockedOnThisRuntime()) return false;
    // Nor with Global Privacy Control or Do Not Track, nor when recording is
    // off in this browser (a stored refusal, or the European/UK default with
    // nothing stored), whoever calls this (code review of #896).
    if (hasBrowserOptOutSignal() || isRecordingOff()) return false;
    const siteId = Number(process.env.REACT_APP_HOTJAR_KEY);
    const version = Number(process.env.REACT_APP_HOTJAR_VERSION);
    if (!siteId || !version) return false;

    installInputMasking();
    Hotjar.init(siteId, version);
    started = true;
    return true;
  } catch (e) {
    return false;
  }
};

export const isSessionReplayStarted = () => started;
